interface PaginationState {
  isVisible: boolean
  currentPage: number
  totalPage: number
  pageSize: number
  pageRange: number[]
}

// GA 이벤트 추적을 위한 Analytics 컴포저블
let analyticsComposable: any = null
let appStore: any = null
let router: any = null

// 클라이언트 사이드에서만 Analytics 초기화
const initializeAnalytics = () => {
  if (process.client && !analyticsComposable) {
    try {
      analyticsComposable = useAnalytics()
      appStore = useAppStore()
      router = useRouter()
    } catch (error) {
      console.warn('Analytics initialization failed:', error)
    }
  }
}

// GA 이벤트 추적 헬퍼 함수
const trackPaginationEvent = (eventName: string, eventData: any) => {
  try {
    if (analyticsComposable?.trackEvent && appStore?.user) {
      analyticsComposable.trackEvent(eventName, {
        ...eventData,
        page_path: router?.currentRoute?.value?.path || window?.location?.pathname || '',
        um_idx: appStore.user.UM_IDX ?? '',
        timestamp: new Date().toISOString()
      })
    }
  } catch (error) {
    console.warn('GA pagination event tracking failed:', error)
  }
}

export const usePagination = () => {
  const state = useState<PaginationState>('pagination', () => ({
    isVisible: false,
    currentPage: 1,
    totalPage: 1,
    pageSize: 10,
    pageRange: [1],
  }))
  
  // Analytics 초기화
  initializeAnalytics()

  const calculatePageRange = () => {
    const start = Math.floor((state.value.currentPage - 1) / 5) * 5 + 1
    const end = Math.min(start + 4, state.value.totalPage)
    state.value.pageRange = Array.from(
      { length: end - start + 1 },
      (_, i) => start + i,
    )
    // 1페이지만 존재하면 페이징 영역 안보이도록 처리
    if (state.value.pageRange.length <= 1) {
      state.value.isVisible = false
      state.value.currentPage = 1
    } else {
      state.value.isVisible = true
    }
  }

  const setPage = (page: number) => {
    if (page >= 1 && page <= state.value.totalPage) {
      const previousPage = state.value.currentPage
      state.value.currentPage = page
      calculatePageRange()
      
      // GA 이벤트: 페이지 직접 설정
      trackPaginationEvent('pagination_page_set', {
        event_category: 'pagination_interaction',
        previous_page: previousPage,
        target_page: page,
        total_pages: state.value.totalPage,
        page_size: state.value.pageSize,
        page_range: state.value.pageRange,
        is_visible: state.value.isVisible
      })
    }
  }

  const next = () => {
    const currentPage = state.value.currentPage
    if (currentPage < state.value.totalPage) {
      trackPaginationEvent('pagination_next_clicked', {
        event_category: 'pagination_interaction',
        current_page: currentPage,
        target_page: currentPage + 1,
        total_pages: state.value.totalPage
      })
    }
    setPage(state.value.currentPage + 1)
  }

  const prev = () => {
    const currentPage = state.value.currentPage
    if (currentPage > 1) {
      trackPaginationEvent('pagination_prev_clicked', {
        event_category: 'pagination_interaction',
        current_page: currentPage,
        target_page: currentPage - 1,
        total_pages: state.value.totalPage
      })
    }
    setPage(state.value.currentPage - 1)
  }

  const initialize = (totalItems: number) => {
    const previousTotalPage = state.value.totalPage
    const previousCurrentPage = state.value.currentPage
    
    state.value.totalPage = Math.ceil(totalItems / state.value.pageSize)
    state.value.currentPage = 1
    calculatePageRange()
    
    // GA 이벤트: 페이지네이션 초기화
    trackPaginationEvent('pagination_initialized', {
      event_category: 'pagination_management',
      total_items: totalItems,
      total_pages: state.value.totalPage,
      page_size: state.value.pageSize,
      previous_total_pages: previousTotalPage,
      previous_current_page: previousCurrentPage,
      is_visible: state.value.isVisible,
      page_range: state.value.pageRange
    })
  }

  const update = (totalItems: number) => {
    const previousTotalPage = state.value.totalPage
    const previousCurrentPage = state.value.currentPage

    state.value.totalPage = Math.ceil(totalItems / state.value.pageSize)

    calculatePageRange()
    if (state.value.currentPage > state.value.totalPage) {
      state.value.currentPage--
    }

    // GA 이벤트: 페이지네이션 초기화
    trackPaginationEvent('pagination_updated', {
      event_category: 'pagination_management',
      total_items: totalItems,
      total_pages: state.value.totalPage,
      page_size: state.value.pageSize,
      previous_total_pages: previousTotalPage,
      previous_current_page: previousCurrentPage,
      is_visible: state.value.isVisible,
      page_range: state.value.pageRange
    })
  }

  return {
    state,
    setPage,
    next,
    prev,
    initialize,
    update,
  }
}
